/**
 * Status Badge Component
 * Displays delivery status with transport-style color coding
 * Enhanced to match Transport module's visual design
 */

interface StatusBadgeProps {
  status: string;
  showStage?: boolean;
  stage?: number;
}

// Transport-style status colors (background colors for badges)
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  // Stage 1 - Request Created
  NEW: { bg: 'bg-gray-100', text: 'text-gray-800' },
  PENDING: { bg: 'bg-gray-100', text: 'text-gray-800' },
  REQUEST_CREATED: { bg: 'bg-gray-100', text: 'text-gray-800' },
  SCHEDULED: { bg: 'bg-gray-100', text: 'text-gray-800' },
  RESCHEDULED: { bg: 'bg-gray-100', text: 'text-gray-800' },

  // Broadcasting
  BROADCASTING: { bg: 'bg-indigo-100', text: 'text-indigo-800' },

  // Stage 2 - Assigned
  ASSIGNED: { bg: 'bg-blue-100', text: 'text-blue-800' },
  ACCEPTED: { bg: 'bg-blue-100', text: 'text-blue-800' },

  // Stage 3 - In Transit
  IN_TRANSIT: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  ARRIVED_PICKUP: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  PICKED_UP: { bg: 'bg-orange-100', text: 'text-orange-800' },

  // Stage 4 - Delivered
  ARRIVED_DROP: { bg: 'bg-orange-100', text: 'text-orange-800' },
  DELIVERED: { bg: 'bg-green-100', text: 'text-green-800' },
  PAYMENT_PENDING: { bg: 'bg-purple-100', text: 'text-purple-800' },

  // Stage 5 - Completed
  PAYMENT_RECEIVED: { bg: 'bg-green-100', text: 'text-green-800' },
  COMPLETED: { bg: 'bg-emerald-100', text: 'text-emerald-800' },

  // Failed/Cancelled
  FAILED: { bg: 'bg-red-100', text: 'text-red-800' },
  CANCELLED: { bg: 'bg-red-100', text: 'text-red-800' },
};

// Stage indicator colors
const STAGE_COLORS: Record<number, string> = {
  0: 'bg-red-100 text-red-600',      // Cancelled/Failed
  1: 'bg-gray-100 text-gray-600',    // Request Created
  2: 'bg-blue-100 text-blue-600',    // Assigned
  3: 'bg-yellow-100 text-yellow-600', // In Transit
  4: 'bg-orange-100 text-orange-600', // Delivered
  5: 'bg-green-100 text-green-600',   // Completed
};

// Human-readable label overrides — keeps the raw status clean but shows a friendlier label
const STATUS_LABELS: Record<string, string> = {
  REQUEST_CREATED: 'Request Created',
  BROADCASTING: 'Broadcasting',
  ASSIGNED: 'Driver Assigned',
  ACCEPTED: 'Driver Accepted',
  IN_TRANSIT: 'In Transit',
  ARRIVED_PICKUP: 'At Pickup',
  PICKED_UP: 'Picked Up',
  ARRIVED_DROP: 'At Drop',
  DELIVERED: 'Awaiting Approval',   // not yet "DELIVERED" from customer's view
  PAYMENT_PENDING: 'Payment Pending',
  PAYMENT_RECEIVED: 'Payment Received',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
  PENDING: 'Pending',
  SCHEDULED: 'Scheduled',
  RESCHEDULED: 'Rescheduled',
};

export function StatusBadge({ status, showStage = false, stage }: StatusBadgeProps) {
  const upperStatus = status?.toUpperCase() || 'PENDING';
  const colors = STATUS_COLORS[upperStatus] || STATUS_COLORS.PENDING;
  const displayStatus = STATUS_LABELS[upperStatus] || status?.replace(/_/g, ' ') || 'Pending';

  return (
    <div className="flex items-center gap-2">
      {showStage && stage !== undefined && (
        <StageIndicator stage={stage} />
      )}
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
        {displayStatus}
      </span>
    </div>
  );
}

interface StageIndicatorProps {
  stage: number;
  size?: 'sm' | 'md';
}

export function StageIndicator({ stage, size = 'sm' }: StageIndicatorProps) {
  const stageColor = STAGE_COLORS[stage] || STAGE_COLORS[1];
  const sizeClasses = size === 'sm' ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm';

  return (
    <div className="flex items-center gap-1">
      <span
        className={`inline-flex items-center justify-center rounded-full font-bold ${sizeClasses} ${stageColor}`}
      >
        {stage === 0 ? 'X' : stage}
      </span>
      <span className="text-xs text-gray-500">/5</span>
    </div>
  );
}

// Export utility functions
export function getStatusColor(status: string): { bg: string; text: string } {
  const upperStatus = status?.toUpperCase() || '';
  return STATUS_COLORS[upperStatus] || STATUS_COLORS.PENDING;
}

export function getStageColor(stage: number): string {
  return STAGE_COLORS[stage] || STAGE_COLORS[1];
}
